import './icons/fontsSprite.css'
import fontSpriteSrc from './icons/fontsSprite.png'

import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { schema, SUPPORTED_FONTS } from 'prose-core'
import { useContext, useEffect, useMemo } from 'react'

import { AlmanacEditorStateContext } from 'stores/EditorState'

import { FontFamilyDropdownBody } from './FontFamilyDropdownBody'
import { FontFamilyMainButton } from './FontFamilyMainButton'
import { FontFamilyContext, FontFamilyStore } from './stores/FontFamilyStore'
import Styles from './FontFamilyGroup.module.scss'
import { FontFamilyType } from 'utils/fontUtils'
import { FontFamilyDropdownButton } from './FontFamilyDropdownButton'
import { SimpleDropdown } from '../SimpleDropdown/SimpleDropdown'
import { SimpleDropdownItem } from '../SimpleDropdown/SimpleDropdownItem'
import { IconName, ToolbarIcon } from 'common/Prose/Toolbar/ToolbarIcon'

interface Props {
  inToolbar?: boolean
}

export const FontFamilyGroup = observer(function FontFamilyDropdown({ inToolbar = false }: Props) {
  const almanacEditorState = useContext(AlmanacEditorStateContext)
  const activeWeights = almanacEditorState?.activeWeights
  const activeFamilies = computed(() => almanacEditorState?.activeFamilies.filter((family) => !!family)).get()
  const hasCodeBlock = computed(() => almanacEditorState?.selectionIncludesSomeNodeTypes(schema.nodes.code_block)).get()
  const fontFamilyStore = useMemo(() => new FontFamilyStore(), [])

  useEffect(() => {
    // This goes to empty and [null] while changing layers
    if (activeFamilies?.length) {
      fontFamilyStore.activeFamilies = activeFamilies
    }
    // This goes empty while changing layers
    if (activeWeights?.length) {
      fontFamilyStore.activeWeights = activeWeights
    }
    fontFamilyStore.hasCodeBlock = hasCodeBlock
  }, [fontFamilyStore, activeFamilies, activeWeights, hasCodeBlock])

  if (inToolbar)
    return (
      <SimpleDropdown
        bodyClassName={Styles.isSubmenuBody}
        aria-label="Change font family"
        placement="right-start"
        isSubmenu
      >
        <SimpleDropdownItem
          icon={<ToolbarIcon name={IconName.Font} />}
          chevronLabel={fontFamilyStore.firstActiveFontDefinition}
          label="Font"
        />
        <FontFamilyContext.Provider value={fontFamilyStore}>
          {SUPPORTED_FONTS.map((fontName: FontFamilyType) => (
            <FontFamilyDropdownButton key={fontName} fontName={fontName} />
          ))}
        </FontFamilyContext.Provider>
      </SimpleDropdown>
    )

  return (
    <FontFamilyContext.Provider value={fontFamilyStore}>
      <FontFamilyMainButton />
      <FloatingPortal id="floating-ui-root">
        {fontFamilyStore.showingFontFamilyDropdown && <FontFamilyDropdownBody />}
      </FloatingPortal>
      <link rel="preload" as="image" href={fontSpriteSrc} />
    </FontFamilyContext.Provider>
  )
})
